import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import { LABEL_MODES } from 'chameleon/legacy/src/components/TeacherOrganize/utils/constants';
import cn from 'classnames';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const LABEL_TEXT = {
  create: `Labels can be added to assigned and unassigned resources to organize them for yourself and for your students.

  Label Name`,
  edit: 'Edit Label',
  noLabelDropdown: 'You have not created any labels to nest under',
  withLabelDropdown: 'Please select parent',
};

const LabelModal = ({
  onClose,
  onCreate,
  onUpdate,
  onLabelNameChange,
  onParentIdChange,
  mode,
  label,
  parentLabels,
  labels,
}) => {
  const [labelHasChanged, setLabelHasChanged] = useState(false);
  const [updatedLabel, setUpdatedLabel] = useState({ labelId: label.labelId }); // Only store the permanent labelId initially
  const prevLabel = usePrevious(label);
  const labelName = label?.name || null;
  const noExistingLabels = parentLabels.length === 0;
  const message = (
    <Fragment>
      <p className="whitespace-pre-line">
        {mode === LABEL_MODES.create ? LABEL_TEXT.create : LABEL_TEXT.edit}
      </p>
      <input
        type="text"
        placeholder="Label Name"
        className="border border-solid rounded-full w-full h-12.6 text-gray-900 pl-4"
        onChange={(e) => onLabelNameChange(e.target.value)}
        {...(labelName !== null && mode === LABEL_MODES.edit
          ? { value: labelName }
          : null)}
      />
      <>
        <p className="pt-4">Nest Under</p>
        <select
          className={cn(
            'border border-solid rounded-full w-full h-12.6 text-gray-900 pl-4',
            {
              italic: noExistingLabels,
            },
          )}
          onChange={(e) => onParentIdChange(e.target.value)}
          {...(label?.parentId !== null
            ? mode === LABEL_MODES.create
              ? { value: label.parentId }
              : { value: label.parentId }
            : mode === LABEL_MODES.edit
              ? { value: 'disabled-select', disabled: true }
              : { value: label.labelId })}
        >
          <option value="">
            {noExistingLabels
              ? LABEL_TEXT.noLabelDropdown
              : LABEL_TEXT.withLabelDropdown}
          </option>
          <option
            value="disabled-select"
            className={label && label.parentId !== null ? 'hidden' : 'block'}
          >
            —
          </option>
          {parentLabels.length > 0 &&
            parentLabels.map((parentLabel) => (
              <option key={parentLabel.labelId} value={parentLabel.labelId}>
                {parentLabel.name}
              </option>
            ))}
        </select>
      </>
    </Fragment>
  );

  const closeModal = () => {
    onClose && onClose();
  };

  const createOrUpdate = () => {
    if (mode === LABEL_MODES.create) {
      onCreate(label);
    } else {
      // We make sure we only send the updated label
      onUpdate(updatedLabel);
    }
    onClose();
  };

  const _getLabelOrder = (parentId) => {
    // Filter the labels that have the same parentId
    const siblingLabels = Object.values(labels).filter(
      (label) => label.parentId === parentId,
    );

    // Ensure that the label order is different from other labels at the same level
    if (siblingLabels.length > 0) {
      return Math.max(...siblingLabels.map((label) => label.order)) + 1;
    }

    const DEFAULT_LABEL_ORDER = 1;
    return DEFAULT_LABEL_ORDER;
  };

  useEffect(() => {
    if (!prevLabel) return; // Skip the first run when prevLabel is undefined

    let hasChanged = false;
    if (prevLabel.name !== label.name) {
      setUpdatedLabel((prevState) => ({ ...prevState, name: label.name }));
      hasChanged = true;
    }
    if (prevLabel.parentId !== label.parentId) {
      const newLabelOrder = _getLabelOrder(label.parentId || null);

      setUpdatedLabel((prevState) => ({
        ...prevState,
        parentId: label.parentId,
        order: newLabelOrder,
      }));
      hasChanged = true;
    }

    if (hasChanged) {
      setLabelHasChanged(true);
    }
  }, [label, prevLabel]);

  const btnPrimary = 'Apply';
  const btnSecondary = 'Cancel';

  return (
    <ConfirmDialog
      size="small"
      title={mode === LABEL_MODES.create ? 'New Label' : 'Edit Label'}
      content={{
        btnPrimary,
        btnSecondary,
      }}
      isPrimaryBtnDisabled={Boolean(!labelHasChanged)}
      onClose={closeModal}
      onSuccess={() => !!labelName.trim() && createOrUpdate()}
    >
      {message}
    </ConfirmDialog>
  );
};

LabelModal.displayName = 'LabelModal';

// Prop types here are not final. Feel free to change/update
LabelModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onLabelNameChange: PropTypes.func.isRequired,
  onParentIdChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf([LABEL_MODES.create, LABEL_MODES.edit]).isRequired,
  label: PropTypes.shape({
    labelId: PropTypes.string,
    name: PropTypes.string,
    parentId: PropTypes.number,
    masterSubjectId: PropTypes.number,
  }),
  parentLabels: PropTypes.arrayOf(
    PropTypes.shape({
      labelId: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
};

LabelModal.defaultProps = {
  label: {},
  parentLabels: [],
};

export default LabelModal;
